<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { GetAdminsResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type User = GetAdminsResponse['payload'][number];

interface Props {
  disabled?: boolean;
  affiliatesTypes?: string[];
  teammatesIds?: string[];
  usersList?: User[] | null;
}

interface Emits {
  'update:teammatesIds': [value: string[]];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  affiliatesTypes: () => [],
  teammatesIds: () => [],
  usersList: () => [],
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const modeArray = [
  'teammates_affiliates',
  'teammates_affiliates_only_selected',
  'teammates_affiliates_except_selected',
];
//#endregion

//#region composables
const { t } = useI18n();
//#endregion

//#region state
const teammatesAffiliatesActive = ref(false);
const teammatesAffiliatesType = ref('');
//#endregion

//#region computed
const pTeammatesIds = computed(() => props.usersList === null ? [] : props.teammatesIds);
//#endregion

//#region methods
function clear() {
  modeArray.forEach((item) => {
    const index = props.affiliatesTypes.indexOf(item);
    if (index >= 0) {
      props.affiliatesTypes.splice(index, 1);
    }
  });
}
//#endregion

//#region watch
watch(() => props.affiliatesTypes, (value) => {
  const curAffiliatesTypes = Array.isArray(value) ? cloneDeep(value) : [];
  const intersection = modeArray.filter((modeItem) => curAffiliatesTypes.includes(modeItem));
  if (intersection.length > 0) {
    teammatesAffiliatesType.value = intersection[0] ?? '';
    teammatesAffiliatesActive.value = true;
  } else {
    teammatesAffiliatesActive.value = false;
  }
}, { immediate: true });

watch(teammatesAffiliatesType, (value) => {
  clear();
  if (value) {
    props.affiliatesTypes.push(value);
    if (value === 'teammates_affiliates') {
      emit('update:teammatesIds', []);
    }
  }
}, { immediate: true });

watch(teammatesAffiliatesActive, (value) => {
  if (!value) {
    teammatesAffiliatesType.value = '';
    emit('update:teammatesIds', []);
  } else if (!teammatesAffiliatesType.value) {
    teammatesAffiliatesType.value = 'teammates_affiliates';
  }
}, { immediate: true });
//#endregion
</script>

<template>
  <div class="permission-row permission-tree">
    <UiCheckbox
      v-model="teammatesAffiliatesActive"
      :disabled="props.disabled"
      :label="t('permissions.teammates_affiliates')"
      class="permission-tree__checkbox"
    />
    <div class="permission-tree__branch">
      <UiRadioGroup
        v-model="teammatesAffiliatesType"
        class="t:w-full"
        :disabled="!teammatesAffiliatesActive || props.disabled"
        :options="[
          {
            label: t('permissions.allow_all'),
            value: 'teammates_affiliates',
          },
          {
            label: t('permissions.allow_selected'),
            value: 'teammates_affiliates_only_selected',
          },
          {
            label: t('permissions.deny_selected'),
            value: 'teammates_affiliates_except_selected',
          }
        ]"
      >
        <UiSelectElement
          :model-value="pTeammatesIds"
          :placeholder="t('permissions.add_teammates')"
          :list="props.usersList || []"
          size="big"
          search-param="email"
          option-label-param="email"
          option-name-param="email"
          filterable
          multiple
          collapse-tag-tooltip
          :max-collapse-tags="1"
          :disabled="!teammatesAffiliatesActive
            || teammatesAffiliatesType === 'teammates_affiliates'
            || props.disabled"
          @update:model-value="emit('update:teammatesIds', $event)"
        />
      </UiRadioGroup>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.permission-row {
  width: 100%;
}

.permission-tree {
  margin-top: 12px;

  &__checkbox {
    margin-right: 16px;
  }

  &__branch {
    position: relative;
    width: 100%;
    padding-left: 24px !important;
    padding-top: 8px;

    &::before,
    &::after {
      position: absolute;
      left: 7px;
      content: '';
      background: #eaeaea;
    }

    &::before {
      top: 0;
      width: 1px;
      height: 15px;
    }

    &::after {
      top: 14px;
      width: 12px;
      height: 1px;
    }
  }
}
</style>
